import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HotelCard from "@/components/HotelCard";
import HotelMap from "@/components/HotelMap";
import { track } from "@/lib/analytics";
import {
    MapPin, ChevronRight, Waves, Plane, Landmark, Anchor,
    Star, LoaderCircle, ChevronDown,
} from "lucide-react";

const POI_META = {
    beach: { icon: Waves, label: "Plaj", color: "cyan" },
    airport: { icon: Plane, label: "Havalimanı", color: "indigo" },
    attraction: { icon: Landmark, label: "Gezi Yeri", color: "amber" },
    marina: { icon: Anchor, label: "Marina", color: "violet" },
};

/** Set document head SEO tags + JSON-LD. */
function useSeo({ name, description, image, slug, breadcrumb }) {
    useEffect(() => {
        if (!name) return;
        const title = `${name} Otelleri | Online Rezervasyon — msonline Travel`;
        document.title = title;

        const setMeta = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
        };

        setMeta('meta[name="description"]', {
            create: { name: "description" },
            set: { content: (description || `${name} bölgesindeki en iyi oteller, fiyat karşılaştırması ve rezervasyon.`).slice(0, 160) },
        });
        setMeta('meta[property="og:title"]', { create: { property: "og:title" }, set: { content: title } });
        setMeta('meta[property="og:description"]', { create: { property: "og:description" }, set: { content: description || `${name} bölgesinde tatil` } });
        setMeta('meta[property="og:type"]', { create: { property: "og:type" }, set: { content: "website" } });
        if (image) setMeta('meta[property="og:image"]', { create: { property: "og:image" }, set: { content: image } });

        // canonical
        let canon = document.head.querySelector('link[rel="canonical"]');
        if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
        canon.href = `${window.location.origin}/destinasyon/${slug}`;

        // JSON-LD structured data
        const oldLd = document.head.querySelector('script[data-seo="dest-jsonld"]');
        if (oldLd) oldLd.remove();
        const ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.dataset.seo = "dest-jsonld";
        const itemList = (breadcrumb || []).map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.name,
            "item": `${window.location.origin}/destinasyon/${b.slug}`,
        }));
        itemList.push({
            "@type": "ListItem",
            "position": itemList.length + 1,
            "name": name,
            "item": `${window.location.origin}/destinasyon/${slug}`,
        });
        ld.text = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "TouristDestination",
                    "name": name,
                    "description": description,
                    "url": `${window.location.origin}/destinasyon/${slug}`,
                    ...(image && { "image": image }),
                },
                {
                    "@type": "BreadcrumbList",
                    "itemListElement": itemList,
                },
            ],
        });
        document.head.appendChild(ld);

        return () => {
            // cleanup on unmount
            const el = document.head.querySelector('script[data-seo="dest-jsonld"]');
            if (el) el.remove();
        };
    }, [name, description, image, slug, JSON.stringify(breadcrumb)]); // eslint-disable-line
}

export default function DestinationLandingPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showAllPOIs, setShowAllPOIs] = useState(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setError(false);
        api.get(`/destinations/${slug}/landing`)
            .then((r) => {
                setData(r.data);
                track("page_view", {
                    destination_slug: slug,
                    destination_name: r.data?.destination?.name || "",
                });
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
        window.scrollTo(0, 0);
    }, [slug]);

    const d = data?.destination;
    useSeo({
        name: d?.name || "",
        description: d?.description || "",
        image: d?.image || "",
        slug: d?.slug || "",
        breadcrumb: data?.breadcrumb || [],
    });

    const groupedPOIs = useMemo(() => {
        if (!data?.pois) return {};
        return data.pois.reduce((acc, p) => {
            const t = p.type || "other";
            acc[t] = acc[t] || [];
            acc[t].push(p);
            return acc;
        }, {});
    }, [data]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
                <Navbar />
                <div className="flex items-center justify-center py-24">
                    <LoaderCircle className="h-10 w-10 animate-spin text-sky-500" />
                </div>
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
                <Navbar />
                <div className="container mx-auto px-4 py-24 text-center">
                    <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-3">Destinasyon Bulunamadı</h1>
                    <p className="text-slate-500 mb-6">Aradığınız destinasyon mevcut değil veya kaldırılmış.</p>
                    <Link to="/" className="inline-block px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">Ana Sayfaya Dön</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-50 dark:bg-slate-950 min-h-screen" data-testid="destination-landing">
            <Navbar />

            {/* Hero */}
            <section
                className="relative h-[55vh] min-h-[400px] w-full bg-slate-900"
                style={{
                    backgroundImage: d.image ? `url(${resolveImageUrl(d.image)})` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
                <div className="absolute inset-0 flex items-end pb-10">
                    <div className="container mx-auto px-4 md:px-6 max-w-7xl">
                        <nav className="flex items-center gap-1 text-sm text-white/80 mb-2 flex-wrap" data-testid="dest-breadcrumb">
                            <Link to="/" className="hover:text-white">Anasayfa</Link>
                            <ChevronRight className="h-3 w-3 opacity-60" />
                            {data.breadcrumb?.map((b) => (
                                <React.Fragment key={b.slug}>
                                    <Link to={`/destinasyon/${b.slug}`} className="hover:text-white">{b.name}</Link>
                                    <ChevronRight className="h-3 w-3 opacity-60" />
                                </React.Fragment>
                            ))}
                            <span className="text-white font-semibold">{d.name}</span>
                        </nav>
                        <h1 className="font-display text-4xl md:text-6xl font-bold text-white tracking-tight" data-testid="dest-title">
                            {d.name}
                        </h1>
                        <p className="text-white/90 text-base md:text-lg max-w-2xl mt-3" data-testid="dest-desc">{d.description}</p>
                        <div className="flex items-center gap-4 mt-5 text-white/90 text-sm flex-wrap" data-testid="dest-stats">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20">
                                <Star className="h-3.5 w-3.5 text-amber-300 fill-amber-300" /> <strong>{data.stats.total_hotels}</strong> otel
                            </span>
                            {data.stats.avg_rating > 0 && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20">
                                    Ortalama puan <strong>{data.stats.avg_rating}/10</strong>
                                </span>
                            )}
                            {d.region && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20">
                                    <MapPin className="h-3.5 w-3.5" /> {d.region}
                                </span>
                            )}
                            <button
                                onClick={() => navigate(`/search?destination=${d.slug}`)}
                                data-testid="dest-search-btn"
                                className="ml-auto px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold shadow-lg shadow-sky-500/30"
                            >
                                Müsait Otelleri Ara →
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sub-destinations */}
            {data.children?.length > 0 && (
                <section className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-testid="dest-children">
                    <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-1">{d.name} Bölgeleri</h2>
                    <p className="text-sm text-slate-500 mb-4">Aşağıdaki alt bölgelerden tatil yapacağınız yeri seçin:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {data.children.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/destinasyon/${c.slug}`}
                                data-testid={`dest-child-${c.slug}`}
                                className="group relative h-32 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800"
                            >
                                {c.image ? (
                                    <img src={resolveImageUrl(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-sky-400 to-indigo-600" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                <div className="absolute inset-0 p-3 flex flex-col justify-end">
                                    <div className="font-display font-bold text-white text-lg">{c.name}</div>
                                    <div className="text-xs text-white/80">{c.hotel_count} otel</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Top Hotels */}
            <section className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-testid="dest-hotels">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">En İyi {d.name} Otelleri</h2>
                        <p className="text-sm text-slate-500">Misafir puanı ve yıldıza göre sıralanmış en popüler tesisler.</p>
                    </div>
                    {data.stats.total_hotels > data.hotels.length && (
                        <Link to={`/search?destination=${d.slug}`} className="text-sm font-bold text-sky-600 hover:text-sky-700">
                            Tümünü gör ({data.stats.total_hotels}) →
                        </Link>
                    )}
                </div>
                {data.hotels.length === 0 ? (
                    <div className="p-8 text-center rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500">
                        Bu bölgede henüz otel kaydı yok. <Link to="/tesisimi-kaydet" className="text-sky-600 font-bold">Otelinizi ekleyin</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.hotels.map((h) => <HotelCard key={h.id} hotel={h} />)}
                    </div>
                )}
            </section>

            {/* POIs grouped */}
            {data.pois?.length > 0 && (
                <section className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-testid="dest-pois">
                    <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-1">Çevredeki Yerler</h2>
                    <p className="text-sm text-slate-500 mb-4">{d.name}'a yakın plajlar, gezi yerleri ve havalimanları.</p>
                    <div className="space-y-5">
                        {Object.entries(groupedPOIs).slice(0, showAllPOIs ? 99 : 4).map(([type, list]) => {
                            const meta = POI_META[type] || { icon: MapPin, label: type, color: "slate" };
                            const Icon = meta.icon;
                            return (
                                <div key={type}>
                                    <h3 className={`text-xs font-bold uppercase tracking-widest text-${meta.color}-600 dark:text-${meta.color}-400 flex items-center gap-1.5 mb-2`}>
                                        <Icon className="h-3.5 w-3.5" /> {meta.label} ({list.length})
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {list.map((p) => (
                                            <div key={p.slug} className={`p-3 rounded-xl bg-${meta.color}-50 dark:bg-${meta.color}-950/20 border border-${meta.color}-100 dark:border-${meta.color}-900/40 flex items-center justify-between gap-2`}>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{p.name}</div>
                                                    {p.description && <div className="text-[11px] text-slate-500 truncate">{p.description}</div>}
                                                </div>
                                                <div className={`text-xs font-bold flex-shrink-0 px-2 py-1 rounded-lg bg-${meta.color}-100 dark:bg-${meta.color}-900/50 text-${meta.color}-700 dark:text-${meta.color}-300`}>
                                                    {p.distance_km < 1 ? `${Math.round(p.distance_km * 1000)} m` : `${p.distance_km.toFixed(1)} km`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {Object.keys(groupedPOIs).length > 4 && (
                        <button onClick={() => setShowAllPOIs(!showAllPOIs)} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-sky-600 hover:text-sky-700">
                            {showAllPOIs ? "Daha az göster" : "Tüm yerleri göster"} <ChevronDown className={`h-4 w-4 transition-transform ${showAllPOIs ? "rotate-180" : ""}`} />
                        </button>
                    )}
                </section>
            )}

            {/* Map */}
            {d.lat && d.lng && (
                <section className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-testid="dest-map">
                    <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-3">{d.name} Konumu</h2>
                    <HotelMap lat={d.lat} lng={d.lng} name={d.name} height={400} zoom={11} />
                </section>
            )}

            {/* FAQ for SEO */}
            <section className="container mx-auto px-4 md:px-6 max-w-7xl py-8" data-testid="dest-faq">
                <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-4">{d.name} Hakkında Sıkça Sorulan Sorular</h2>
                <div className="space-y-2">
                    {[
                        { q: `${d.name}'da kaç otel bulabilirim?`, a: `${d.name} bölgesinde toplam ${data.stats.total_hotels} otel listemizde bulunmaktadır. Müşteri puanı, yıldız, fiyat ve özelliklere göre filtreleyebilirsiniz.` },
                        { q: `${d.name}'da en uygun fiyatlı tatil nasıl yapılır?`, a: `Erken rezervasyon kampanyalarını takip ederek, yumuşak sezon (ilkbahar-sonbahar başı) tarihlerini seçerek ve esnek iptal koşullu fiyat planlarıyla %30-50 tasarruf edebilirsiniz.` },
                        { q: `${d.name} hangi bölgede yer alıyor?`, a: `${d.name}, ${d.region || "Türkiye"} bölgesinde yer almaktadır${data.breadcrumb.length ? `. ${data.breadcrumb.map(b => b.name).join(" › ")} sınırları içerisindedir` : ""}.` },
                        { q: `Online rezervasyon güvenli mi?`, a: `Tüm rezervasyonlar SSL şifreleme ile korunur. Banka POS entegrasyonumuz 3D Secure ile çalışır, kredi kartı bilgileriniz tarafımızca saklanmaz.` },
                    ].map((item, i) => (
                        <details key={i} className="group p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-white flex items-center justify-between">
                                {item.q}
                                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </summary>
                            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{item.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}
